<template>
  <header>
    <div class="logo" @click="handleGotoHome()">
      <img src="@/assets/logo.png" />
      UPDF
    </div>
    <span class="title">{{ $t('Account Center') }}</span>
    <el-popover v-model:visible="profileDropdownVisible" placement="bottom-end" width="300px" popper-class="no-padding" trigger="click">
      <template #default>
        <ProfileDropdown :ignore-actions="['accountCenter']" @on-action="profileDropdownVisible = false"></ProfileDropdown>
      </template>
      <template #reference>
        <div class="user-info">
          <Avatar
            :size="40"
            :can-edit="false"
            :company-user="userInfo.companyUser"
            :color="userInfo?.color"
            :src="userInfo?.headerImg"
            :nickname="userStore.nickName">
          </Avatar>
        </div>
      </template>
    </el-popover>
    <button class="menu-toggle" @click="handleToggleLeftMenu"><img src="@/assets/icon-toggle.svg" v-svg-inline /></button>
  </header>
  <div class="account-center">
    <Transition name="fade">
      <div class="screen" v-if="leftMenuVisible" @click="handleToggleLeftMenu"></div>
    </Transition>
    <div class="account-center-container">
      <div class="left-menu" :class="{ show: leftMenuVisible }">
        <div class="menu" @click="handleClickMenu">
          <RouterLink class="menu-item profile" :class="{ active: routeName === 'accountCenterProfile' }" :to="{ name: 'accountCenterProfile', query: routeQuery }">{{ $t('Account Information') }}</RouterLink>
          <RouterLink class="menu-item plans" :class="{ active: routeName === 'accountCenterPlans' }" :to="{ name: 'accountCenterPlans', query: routeQuery }">{{ $t('Products and Plans') }}</RouterLink>
          <RouterLink v-if="canRedeem" class="menu-item redeem" :class="{ active: routeName === 'accountCenterRedeem' }" :to="{ name: 'accountCenterRedeem', query: routeQuery }">{{ $t('Redeem License') }}</RouterLink>
          <RouterLink v-if="!userStore.companyUser" class="menu-item orders" :class="{ active: routeName === 'accountCenterOrders' }" :to="{ name: 'accountCenterOrders', query: routeQuery }">{{ $t('Payment and Order') }}</RouterLink>
          <RouterLink class="menu-item devices" :class="{ active: routeName === 'accountCenterDevices' }" :to="{ name: 'accountCenterDevices', query: routeQuery }">{{ $t('Device Management') }}</RouterLink>
          <RouterLink class="menu-item settings" :class="{ active: routeName === 'accountCenterSettings' }" :to="{ name: 'accountCenterSettings', query: routeQuery }">{{ $t('Account Settings') }}</RouterLink>
          <span class="menu-close-btn" @click="handleToggleLeftMenu"></span>
        </div>
      </div>
      <main v-loading="loading">
        <el-scrollbar>
          <router-view v-if="!loading"></router-view>
        </el-scrollbar>
      </main>
    </div>
  </div>
</template>

<script setup>
import { getUserInfo } from '@/api/user/user'
import { isLogin, redirectToLogin } from '@/services/base'
import { logOutSuccess, setLoginResultTmp } from '@/services/login'
import { appStore, userStore } from '@/stores'
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import documentReady from 'document-ready'
import { postMessageToOpenerWindow } from '@/libs/utils'
import config from '@/config'
import Avatar from '@/components/user/avatar.vue'
import ProfileDropdown from '@/view/iframe/profile-dropdown.vue'
import { ElMessage } from 'element-plus'
import enums from '@/libs/enums'

const route = useRoute()
const routeName = computed(() => route.name)
const routeQuery = computed(() => route.query)
const userInfo = computed(() => userStore.userInfo)
const profileDropdownVisible = ref(false)

const { $t } = getCurrentInstance().proxy

const loading = ref(true)
const handleRefreshUserInfo = async () => {
  try {
    const { data } = await getUserInfo()
    Object.assign(userStore.userInfo, data.userInfo)
    // 中文情况下firstName为空则显示nickName的值
    if (appStore.isCN && data.userInfo.firstName === '') {
      userStore.userInfo.firstName = data.userInfo.nickName
    }
  } catch (err) {
    console.log(err)
    if ([2404].includes(err.code)) {
      logOutSuccess()
    }
  }
  loading.value = false
}

const leftMenuVisible = ref(false)
const handleToggleLeftMenu = () => {
  leftMenuVisible.value = !leftMenuVisible.value
  return false
}
const handleClickMenu = event => {
  if (leftMenuVisible.value && event.target?.classList?.contains('menu-item')) {
    handleToggleLeftMenu()
  }
}

const handleGotoHome = () => {
  window.open(enums.websiteUrl.homeLangLink, '_target')
}

const canRedeem = !userStore.companyUser && !appStore.isAppStore
onMounted(() => {
  // 通知父页面dom已加载完毕，可以停止loading
  documentReady(() => {
    postMessageToOpenerWindow(config.iframeReadyActionName)
  })
  // 登录成功后，清除登录结果缓存
  setLoginResultTmp(null)
  if (isLogin()) {
    document.title = 'UPDF ' + $t('Account Center')
    handleRefreshUserInfo()
  } else {
    ElMessage.error({
      message: $t('Please login first'),
      duration: 3000,
    })
    setTimeout(() => {
      redirectToLogin()
    }, 2000)
    
  }
})
</script>
<style lang="scss">
@use 'base.scss';
</style>

<style lang="scss" scoped>
header {
  display: flex;
  align-items: center;
  background: var(--BG-2, #EAEAEA);
  height: 32px;
  padding: 16px 20px;
  color: var(--BG-9, #474747);
  font-size: 16px;
  position: sticky;
  top: 0;
  z-index: 2;
  .logo {
    display: flex;
    align-items: center;
    cursor: pointer;
    img {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      margin-right: 6px;
    }
  }
  .title {
    display: inline-flex;
    align-items: center;
    height: 16px;
    margin-left: 12px;
    padding-left: 12px;
    box-sizing: border-box;
    border-left: 2px solid var(--BG-4divider, #CCCCCC);
  }
  .user-info, .menu-toggle {
    margin-left: auto;
    cursor: pointer;
    height: 40px;
  }
  .menu-toggle {
    display: none;
    border: none;
    background: none;
    width: 24px;
    height: 24px;
    outline: none;
  }
}
.screen {
  position: fixed;
  inset: 0;
  background: #00000099;
  z-index: 1;
  display: none;
  top: 64px;
}
.menu-control {
  display: none;
}
.left-menu {
  height: min-content;
  display: flex;
  flex-direction: column;
  margin-right: 20px;
  position: sticky;
  top: 64px;
  width: 240px;
  .menu {
    .menu-item {
      margin-top: 10px;
      padding: 8px;
      box-sizing: border-box;
      height: 40px;
      display: flex;
      align-items: center;
      border-radius: 10px;
      font-size: 14px;
      text-decoration: unset;
      color: var(--BG-10, #222222);
      transition: all 0.3s;
      &:first-child {
        margin-top: 0;
      }
      &.divider {
        margin-top: 21px;
        position: relative;
        &::after {
          position: absolute;
          content: '';
          display: block;
          width: calc(100% - 16px);
          height: 1px;
          left: 8px;
          top: -11px;
          background: #0000000F;
        }
      }
      .icon {
        width: 24px;
        height: 24px;
        margin-right: 8px;
      }
      &::before {
        content: '';
        display: block;
        width: 24px;
        height: 24px;
        margin-right: 8px;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }
      &.profile {
        &::before {
          background-image: url('@/assets/icon-center-menu-profile.svg');
        }
        &.active::before {
          background-image: url('@/assets/icon-center-menu-profile-on.svg');
        }
      }
      &.plans {
        &::before {
          background-image: url('@/assets/icon-center-menu-plan.svg');
        }
        &.active::before {
          background-image: url('@/assets/icon-center-menu-plan-on.svg');
        }
      }
      &.orders {
        &::before {
          background-image: url('@/assets/icon-center-menu-order.svg');
        }
        &.active::before {
          background-image: url('@/assets/icon-center-menu-order-on.svg');
        }
      }
      &.devices {
        &::before {
          background-image: url('@/assets/icon-center-menu-device.svg');
        }
        &.active::before {
          background-image: url('@/assets/icon-center-menu-device-on.svg');
        }
      }
      &.settings {
        &::before {
          background-image: url('@/assets/icon-center-menu-setting.svg');
        }
        &.active::before {
          background-image: url('@/assets/icon-center-menu-setting-on.svg');
        }
      }
      &.redeem {
        &::before {
          background-image: url('@/assets/icon-center-menu-redeem.png');
        }
        &.active::before {
          background-image: url('@/assets/icon-center-menu-redeem-on.png');
        }
      }
      &.active {
        font-weight: bold;
        background: var(--BG-0, #ffffff);
      }
      &:hover:not(.active) {
        background: var(--BG-3, #dddddd);
      }
      &:active:not(.active) {
        background: #CCCCCC;
      }
    }
  }
}
.account-center {
  background: var(--BG-2, #eaeaea);
  display: flex;
  height: calc(100vh - 64px);
  .account-center-container {
    max-width: 1250px;
    height: 100%;
    padding-bottom: 24px;
    box-sizing: border-box;
    width: 95%;
    margin: 0 auto;
    display: flex;
    main {
      flex: 1;
      height: 100%;
      background: var(--BG-1normal, #f5f5f5);
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
  }
}
@media screen and (max-width: 768px) {
  header {
    .user-info {
      display: none;
    }
    .menu-toggle {
      display: block;
    }
  }
  .left-menu {
    position: fixed;
    left: 0;
    top: 64px;
    z-index: 2;
    height: max-content;
    width: 100vw;
    padding: 16px 12px;
    box-sizing: border-box;
    background: var(--BG-2, #EAEAEA);
    border-radius: 0;
    margin: 0 !important;
    transition: all .5s;
    transform: translateY(-20px);
    visibility: hidden;
    opacity: 0;
    &.show {
      visibility: visible;
      transform: translateX(0);
      opacity: 1;
    }
    h1 {
      .menu-close-btn {
        display: block;
      }
    }
  }
  .screen {
    display: block;
    z-index: 2;
  }
  .section {
    padding: 16px 20px;
  }
  .account-center {
    &-container {
      padding: 0;
      width: 100%;
      flex-direction: column;
      main {
        border-radius: 0;
      }
    }
  }
}
</style>
<style lang="scss">
.ar-SA {
  header {
    .logo {
      img {
        margin-right: 0;
        margin-left: 6px;
      }
    }
    .user-info {
      margin-right: auto;
      margin-left: unset;
    }
    .title {
      margin-left: unset;
      margin-right: 12px;
      padding-left: unset;
      padding-right: 12px;
      border-left: unset;
      border-right: 2px solid var(--BG-4divider, #CCCCCC);
    }
    .menu-toggle {
      margin-right: auto;
      margin-left: unset;
    }
  }
  .left-menu {
    margin-right: 0;
    margin-left: 20px;
    .menu-item {
      &::before {
        margin-right: 0;
        margin-left: 8px;
      }
    }
  }
}
</style>
